/**
 * Streak tracking utility.
 * Provides functions for calculating and updating user activity streaks.
 * A streak counts consecutive days with activity (workout, check-in, or meal logged).
 */
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../services/firebase';

const USERS_COLLECTION = 'users';

/**
 * Get today's date as YYYY-MM-DD string.
 * @returns {string}
 */
export const getTodayDateString = () => {
  const today = new Date();
  return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
};

/**
 * Get yesterday's date as YYYY-MM-DD string.
 * @returns {string}
 */
export const getYesterdayDateString = () => {
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  return `${yesterday.getFullYear()}-${String(yesterday.getMonth() + 1).padStart(2, '0')}-${String(yesterday.getDate()).padStart(2, '0')}`;
};

/**
 * Calculate the new streak values based on the last active date.
 * - If lastActiveDate is today: no change (already counted)
 * - If lastActiveDate is yesterday: increment streak
 * - If lastActiveDate is older or missing: reset to 1
 *
 * @param {number} currentStreak - The current streak count.
 * @param {number} longestStreak - The longest streak ever achieved.
 * @param {string|null} lastActiveDate - The last active date as YYYY-MM-DD.
 * @returns {{ currentStreak: number, longestStreak: number, lastActiveDate: string }}
 */
export const calculateStreak = (currentStreak = 0, longestStreak = 0, lastActiveDate = null) => {
  const today = getTodayDateString();
  const yesterday = getYesterdayDateString();

  // Already active today - no change
  if (lastActiveDate === today) {
    return { currentStreak, longestStreak, lastActiveDate: today };
  }

  let newStreak;

  if (lastActiveDate === yesterday) {
    // Continue the streak
    newStreak = currentStreak + 1;
  } else {
    // Streak broken or first activity ever
    newStreak = 1;
  }

  const newLongest = Math.max(longestStreak, newStreak);

  return {
    currentStreak: newStreak,
    longestStreak: newLongest,
    lastActiveDate: today,
  };
};

/**
 * Update the streak for a user in Firestore.
 * Called when user logs a workout, check-in, or meal.
 *
 * @param {string} userId - The user's ID.
 * @param {Object} profile - The user's current profile data.
 * @param {number} [profile.currentStreak] - Current streak count.
 * @param {number} [profile.longestStreak] - Longest streak ever.
 * @param {string} [profile.lastActiveDate] - Last active date as YYYY-MM-DD.
 * @returns {Promise<{currentStreak: number, longestStreak: number, lastActiveDate: string}>}
 */
export const updateStreak = async (userId, profile = {}) => {
  try {
    const streakData = calculateStreak(
      profile.currentStreak || 0,
      profile.longestStreak || 0,
      profile.lastActiveDate || null
    );

    const userRef = doc(db, USERS_COLLECTION, userId);
    await updateDoc(userRef, {
      currentStreak: streakData.currentStreak,
      longestStreak: streakData.longestStreak,
      lastActiveDate: streakData.lastActiveDate,
    });

    return streakData;
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('[Update streak]', error);
    }
    // Return current values if update fails - non-critical
    return {
      currentStreak: profile.currentStreak || 0,
      longestStreak: profile.longestStreak || 0,
      lastActiveDate: profile.lastActiveDate || null,
    };
  }
};
